import { ReactNode, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getAppConfig } from '@bts-web/core-app-config';
import { css } from '@bts-web/utils-style-engine';
import {
  capitalizeFirstLetter,
  intlAssetValueFormatting,
} from '@bts-web/utils-formatting';
import { Locale, useClientTranslation } from '@bts-web/utils-lokalise';
import { TradeControllerVariants } from '../../../utils';
import {
  MainTradeFormErrorBox,
  wrapperElementStyles,
} from '../../MainTradeFormErrorBox/MainTradeFormErrorBox';
import { TradeInputErrorType } from '../hooks/useTradeInput';
import { TradeAssetDetails } from '../types';
import { MinimumTradeAmountType } from '../../types';
import { TARGET_PRICE_KEY } from '../../constants';
import { getAssetDisplayValueFromSource } from '../../../../common/utils/amounts/getAssetDisplayValueFromSource';
import { getAssetTradeUrl } from '../../../../route-builders';

interface MainTradeFormErrorProps {
  transactionType: TradeControllerVariants;
  errors: TradeInputErrorType;
  assetDetails: TradeAssetDetails;
  openBuyTab?: () => void;
  locale: Locale;
  minimumTradeAmount: MinimumTradeAmountType;
  customMaxFiatAmount?: string;
}

export const MainTradeFormError = ({
  transactionType,
  locale,
  errors,
  assetDetails,
  openBuyTab,
  minimumTradeAmount,
  customMaxFiatAmount,
}: MainTradeFormErrorProps) => {
  const { currency } = getAppConfig();

  const isInStakePage = usePathname()?.includes('/stake') ?? false;

  const router = useRouter();

  const searchParams = useSearchParams();

  const { t } = useClientTranslation();

  const { tooHigh, tooLow, customError } = errors;

  const displayMinimumTradeAmount =
    transactionType !== 'stake' && !searchParams.has(TARGET_PRICE_KEY);

  const formattedMinimumAmount = useMemo(() => {
    return transactionType === 'swap' || transactionType === 'stake'
      ? getAssetDisplayValueFromSource({
          locale,
          sourceAmount: minimumTradeAmount.ASSET?.value as string,
          sourceType: 'ASSET',
          customSymbol: assetDetails.symbol as string,
        })
      : intlAssetValueFormatting(minimumTradeAmount.FIAT?.value ?? 0, {
          currency: currency,
          locale,
        });
  }, [locale, minimumTradeAmount, currency, assetDetails.symbol]);

  const onBuyClick = () => {
    const buyUrl = getAssetTradeUrl(assetDetails.id, { type: 'buy' });

    if (isInStakePage) {
      router.push(buyUrl);
    } else {
      if (openBuyTab) {
        openBuyTab();
      } else {
        router.push(buyUrl);
      }
    }
  };

  let errorMessage: ReactNode = customError ?? null;

  if (tooLow) {
    errorMessage = t('inline_error_amount_too_low');

    if (transactionType === 'stake') {
      errorMessage = t('stake_error_amount_too_low', {
        minimumStakeableAmount: `${
          minimumTradeAmount.ASSET?.value ?? ''
        } ${assetDetails.symbol ?? ''}`,
      });
    }

    if (transactionType === 'savings') {
      errorMessage = t('inline_error_amount_too_low', {
        min_amount: minimumTradeAmount.FIAT?.value ?? '',
        currency,
      });
    }
  }

  if (tooHigh) {
    errorMessage = customMaxFiatAmount
      ? t('inline_error_amount_too_high_with_max', {
          max_amount: intlAssetValueFormatting(customMaxFiatAmount, {
            currency,
            locale,
          }),
        })
      : t('inline_error_amount_too_high');

    if (transactionType === 'stake') {
      errorMessage = (
        <>
          {t('stake_error_amount_too_high')}{' '}
          <button
            type="button"
            className={css({
              padding: 0,
              border: 0,
              backgroundColor: 'transparent',
              color: 'warning.text_primary',
              fontSize: 'caption.small',
              fontWeight: 'caption.small',
              letterSpacing: 'caption.small',
              lineHeight: 'caption.small',
              textDecoration: 'underline',
              cursor: 'pointer',
            })}
            onClick={onBuyClick}
          >
            <strong>
              {t('buy_asset', { asset: assetDetails.symbol ?? '' })}
            </strong>
          </button>
        </>
      );
    }
  }

  if (errorMessage) {
    return <MainTradeFormErrorBox message={errorMessage} />;
  }

  if (!displayMinimumTradeAmount) {
    return null;
  }

  return (
    <div
      className={css({
        ...wrapperElementStyles,
        color: 'neutrals.text_secondary',
      })}
    >
      {`${capitalizeFirstLetter(t('minimum'))}: ${formattedMinimumAmount}`}
    </div>
  );
};
